import { CihSectionHeader } from "~/components/cih/section-header";
import { MarkdownContent } from "~/components/markdown-content";

type Result = {
  id: string;
  title: string;
  description: string | null;
};

export type OutcomesProps = {
  /**
   * Profile-level outcomes content. Both fields are optional. The section is
   * profile-driven (not offer-driven) so it's available to every coach, not
   * just those with Feature.OfferPage. The offer detail page renders its own
   * results fields independently.
   */
  outcomesHeadline: string | null;
  outcomesDescription: string | null;
  /** Tile body — manually curated by the coach. */
  coachingResults: Result[];
  sectionNumber: number;
};

export function CoachOutcomesSection({
  outcomesHeadline,
  outcomesDescription,
  coachingResults,
  sectionNumber,
}: OutcomesProps) {
  const intro = outcomesDescription?.trim() || null;
  const hasTiles = coachingResults.length > 0;

  if (!intro && !hasTiles) return null;

  const headline = outcomesHeadline?.trim() || "What clients walk away with";

  return (
    <section id="outcomes" className="border-t border-gray-200">
      <CihSectionHeader
        label={`${String(sectionNumber).padStart(2, "0")} · Outcomes`}
        title={headline}
      />
      <div className="mx-auto max-w-7xl px-4 pb-14 md:pb-20 xl:px-0">
        {intro && (
          <div className="max-w-prose text-gray-700">
            <MarkdownContent ugc content={intro} />
          </div>
        )}
        {hasTiles && (
          <div className={`grid gap-8 md:grid-cols-3 ${intro ? "mt-10" : ""}`}>
            {coachingResults.map((r) => (
              <div key={r.id} className="border-l-2 border-purple-200 pl-4">
                <h3 className="font-heading text-xl font-medium tracking-tight text-gray-900">
                  {r.title}
                </h3>
                {r.description && (
                  <p className="mt-1 text-sm text-gray-600">{r.description}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
